import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getInvoices, getInvoicePdf, updateInvoiceStatus } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Plus, Eye, FileDown, Printer, MoreHorizontal, Send, CheckCircle, XCircle, Clock, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getInvoiceStatusClass } from '@/lib/utils';
import { API_BASE } from '@/lib/api';
import SmartSearch from '@/components/SmartSearch';

const statuses = ['Draft', 'Sent', 'Partially Paid', 'Paid', 'Overdue', 'Cancelled'];

const statusIcons = {
  'Draft': FileText,
  'Sent': Send,
  'Partially Paid': Clock,
  'Paid': CheckCircle,
  'Overdue': AlertTriangle,
  'Cancelled': XCircle
};

// Column definitions for smart search
const searchColumns = [
  { key: 'invoice_number', label: 'Invoice #', type: 'text' },
  { key: 'client_name', label: 'Client', type: 'text' },
  { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
  { key: 'due_date', label: 'Due Date', type: 'date' },
  { key: 'total_amount', label: 'Total Amount', type: 'number' },
  { key: 'amount_paid', label: 'Amount Paid', type: 'number' },
  { key: 'balance_due', label: 'Balance Due', type: 'number' },
  { key: 'status', label: 'Status', type: 'select', options: statuses.map(s => ({ value: s, label: s })) },
  { key: 'service_order_number', label: 'Service Order', type: 'text' },
  { key: 'trn', label: 'TRN', type: 'text' },
];

// Quick filter presets
const quickFilters = [
  { label: 'Unpaid', filters: { status: 'Sent' } },
  { label: 'Overdue', filters: { status: 'Overdue' } },
  { label: 'Paid', filters: { status: 'Paid' } },
  { label: 'Partially Paid', filters: { status: 'Partially Paid' } },
  { label: 'Draft', filters: { status: 'Draft' } },
  { label: 'This Month', filters: { invoice_date: 'thisMonth' } },
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status');
  
  const [allInvoices, setAllInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const params = { limit: 1000 };
      const res = await getInvoices(params);
      setAllInvoices(res.data.items);
      setFilteredInvoices(res.data.items);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await getInvoicePdf(invoiceId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = (invoiceId) => {
    window.open(`${API_BASE}/invoices/${invoiceId}/print`, '_blank');
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await updateInvoiceStatus(invoiceId, newStatus);
      toast.success(`Invoice status updated to ${newStatus}`);
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Invoices</h1>
          <p className="text-muted-foreground">Manage invoices and track payments</p>
        </div>
        <Button onClick={() => navigate('/invoices/new')} data-testid="new-invoice-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <SmartSearch
            data={allInvoices}
            columns={searchColumns}
            onFilter={setFilteredInvoices}
            quickFilters={quickFilters}
            placeholder="Search by invoice #, client, amount, status..."
          />
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell className="font-mono">
                        <Link to={`/invoices/${inv.id}`} className="hover:text-primary">
                          {inv.invoice_number}
                        </Link>
                      </TableCell>
                      <TableCell className="font-medium">{inv.client_name}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(inv.invoice_date)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(inv.due_date)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(inv.total_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-green-500">{formatCurrency(inv.amount_paid)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        <span className={inv.balance_due > 0 ? 'text-red-500' : ''}>
                          {formatCurrency(inv.balance_due)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getInvoiceStatusClass(inv.status)}>{inv.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => navigate(`/invoices/${inv.id}`)} data-testid={`view-invoice-${inv.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}>
                            <FileDown className="w-4 h-4" />
                          </Button>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`invoice-menu-${inv.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handlePrint(inv.id)}>
                                <Printer className="w-4 h-4 mr-2" />
                                Print
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <Send className="w-4 h-4 mr-2" />
                                  Change Status
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {statuses.map((s) => {
                                    const Icon = statusIcons[s];
                                    return (
                                      <DropdownMenuItem 
                                        key={s} 
                                        onClick={() => handleStatusChange(inv.id, s)}
                                        disabled={inv.status === s}
                                      >
                                        {Icon && <Icon className="w-4 h-4 mr-2" />}
                                        {s}
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredInvoices.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No invoices found matching your search criteria
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredInvoices.length} of {allInvoices.length} invoices
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
